/**
 * Management-agent RPC group.
 *
 * Endpoints in this group are gated by `MgmtAgentMiddleware` (gateway-token
 * auth from the VPS-side management agent). Never expose any of these RPCs
 * via user-facing client paths.
 *
 * Audit storage: every call appends a row to `installation_config_log`
 * (existing append-only audit table) using `eventKind = "commit"` — chosen
 * over a new dedicated table to avoid a fresh enum migration (the
 * config-log enum requires the two-deploy isolation rule per the @fm/db
 * enum-isolation policy) and because the existing surface already carries
 * `installationId`, `author`, `createdAt`, plus structured `filesChanged`
 * + `trigger` JSON for connectionId/status. The pattern matches how
 * `admin.installation.connections.upsert/delete` already audit.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { FetchBackupCredentialsResponse } from "@fm/cluster/services/backup-creds-schema";
import { OcStatsReport } from "@fm/db/models/AgentInstallationStats";
import { InstallationConfigEventKind } from "@fm/db/models/InstallationConfigLog";
import { ConnectionType } from "@fm/db/models/InstallationConnection";
import { InstallationProfilePublish } from "@fm/db/models/InstallationProfilePublish";
import { Schema } from "effect";

import { ReportBackupSnapshotsInput } from "./backup-restore.schemas.ts";
import {
  BackupCredentialMintFailed,
  BackupStorageNotConfigured,
  CryptoError,
  DatabaseError,
  Forbidden,
  RecordNotFoundError,
  Unauthorized,
  ValkeyError,
} from "./errors.ts";
import { MgmtAgentMiddleware } from "./middleware/mgmt-agent.tags.ts";
import { InvalidTriggerPayload } from "./profile.ts";

/**
 * Input payload for `mgmt.fetchConnectionSecret`.
 *
 * @since 1.0.0
 */
export const FetchConnectionSecretInput = Schema.Struct({
  connectionId: Schema.String,
});
export type FetchConnectionSecretInput = Schema.Schema.Type<typeof FetchConnectionSecretInput>;

/**
 * A decrypted connection secret field map: field-name → plaintext value (or
 * null). The KEY names are the SecretRef field names the ProfileRenderer emits
 * (`<connectionId>::<field>`), so a Slack connection carries `botToken` /
 * `appToken` / `signingSecret`, MS Teams carries `appId` / `clientSecret` /
 * `tenantId`, etc. Populated from the row's encrypted `secretFields` column.
 *
 * @since 1.0.0
 */
export const ConnectionSecretFieldMap = Schema.Record({
  key: Schema.String,
  value: Schema.NullOr(Schema.String),
});
export type ConnectionSecretFieldMap = Schema.Schema.Type<typeof ConnectionSecretFieldMap>;

/**
 * Wire response for `mgmt.fetchConnectionSecret`. `accessToken` and
 * `refreshToken` carry plaintext credentials; the value is null when the
 * connection has no stored token of that kind.
 *
 * `fields` is the decrypted multi-field secret map (CLA-745), present only for
 * connections whose `secretFields` column is populated (Slack/MS Teams and
 * other 3+-secret providers). Legacy connections (discord and everything that
 * fits the two token slots) omit it, and the agent falls back to the fixed
 * `{ token, refreshToken }` document — so older agents that ignore `fields` are
 * unaffected.
 *
 * @since 1.0.0
 */
export const FetchConnectionSecretResponse = Schema.Struct({
  connectionId: Schema.String,
  installationId: Schema.String,
  provider: Schema.String,
  accountKey: Schema.String,
  accessToken: Schema.NullOr(Schema.String),
  refreshToken: Schema.NullOr(Schema.String),
  scopes: Schema.NullOr(Schema.Array(Schema.String)),
  fields: Schema.optional(ConnectionSecretFieldMap),
});
export type FetchConnectionSecretResponse = Schema.Schema.Type<typeof FetchConnectionSecretResponse>;

/**
 * Input payload for `mgmt.fetchEffectiveProfile`.
 *
 * @since 1.0.0
 */
export const FetchEffectiveProfileInput = Schema.Struct({
  installationId: Schema.String,
});
export type FetchEffectiveProfileInput = Schema.Schema.Type<typeof FetchEffectiveProfileInput>;

/**
 * Connection metadata returned alongside the publish row. Carries just
 * enough for the agent to loop `mgmt.fetchConnectionSecret(connectionId)`
 * per connection.
 *
 * @since 1.0.0
 */
export const FetchEffectiveProfileConnection = Schema.Struct({
  id: Schema.String,
  provider: Schema.String,
  accountKey: Schema.String,
  type: ConnectionType,
});
export type FetchEffectiveProfileConnection = Schema.Schema.Type<typeof FetchEffectiveProfileConnection>;

/**
 * Wire response for `mgmt.fetchEffectiveProfile`. Carries the
 * `is_current` snapshot row from `installation_profile_publishes`.
 * `renderedProfileJson` is opaque JSON; the renderer schema is the
 * source of truth for its shape. Secrets are NOT included in this
 * response — the agent fetches them separately via
 * `mgmt.fetchConnectionSecret` keyed by connection id.
 *
 * @since 1.0.0
 */
export const FetchEffectiveProfileResponse = Schema.Struct({
  publish: InstallationProfilePublish.json,
  connections: Schema.Array(FetchEffectiveProfileConnection),
  /**
   * runOnceKeys of every prior successful `cli_bootstrap` migration
   * event recorded on this installation's `installation_config_log`.
   * The agent uses this set to short-circuit `runCliBootstrap` for
   * steps that have already been applied — the persisted audit-log
   * row is the durable idempotency record per the docstring on
   * `MigrationEventName."cli_bootstrap"`.
   *
   * @since 1.0.0
   */
  completedCliBootstrapKeys: Schema.Array(Schema.String),
});
export type FetchEffectiveProfileResponse = Schema.Schema.Type<typeof FetchEffectiveProfileResponse>;

/**
 * Status reported by the management agent after an applyProfile run.
 *
 * - `applied` — every connection's encrypted blob was written and
 *   `openclaw secrets reload` returned 0.
 * - `rolled_back` — at least one blob was written but `secrets reload`
 *   failed; every prior `.enc.json` was restored.
 * - `failed` — apply aborted before any successful reload (sops missing,
 *   age key missing, or write error).
 *
 * @since 1.0.0
 */
export const ProfileApplyStatus = Schema.Literal("applied", "rolled_back", "failed");
export type ProfileApplyStatus = Schema.Schema.Type<typeof ProfileApplyStatus>;

/**
 * Input payload for `mgmt.reportProfileApplyResult`. The agent calls
 * this exactly once per applyProfile dispatch with the outcome.
 *
 * @since 1.0.0
 */
export const ReportProfileApplyResultInput = Schema.Struct({
  installationId: Schema.String,
  publishId: Schema.String,
  version: Schema.Int,
  status: ProfileApplyStatus,
  errorTag: Schema.NullOr(Schema.String),
  errorMessage: Schema.NullOr(Schema.String),
});
export type ReportProfileApplyResultInput = Schema.Schema.Type<typeof ReportProfileApplyResultInput>;

/**
 * Wire response for `mgmt.reportProfileApplyResult`. Acknowledges the
 * report — the audit row is the durable record.
 *
 * @since 1.0.0
 */
export const ReportProfileApplyResultResponse = Schema.Struct({
  ok: Schema.Boolean,
});
export type ReportProfileApplyResultResponse = Schema.Schema.Type<typeof ReportProfileApplyResultResponse>;

/**
 * Discrete migration step the agent emits while transitioning the
 * Phase-1 plaintext credentials/ layout to the Phase-2 sops layout.
 * Recorded onto `installation_config_log` so the audit timeline shows
 * the sequence start → both-layouts-on-disk → completed | rolled-back.
 *
 * @since 1.0.0
 */
export const MigrationEventName = Schema.Literal(
  "migration_started",
  "both_layouts_written",
  "migration_completed",
  "migration_rolled_back",
  "migration_failed",
  "plugin_install",
  /**
   * Per-slug managed `openclaw skills install <slug>` outcome. Each
   * entry on `RenderedProfile.skillsInstall` produces exactly one
   * `skill_install` event after the apply step — `errorTag` is null
   * for success/skip and `"SkillInstallFailed"` on non-zero exit. The
   * structured per-slug detail (`slug`, `exitCode`, `stdoutTail`,
   * `stderrTail`) rides on the optional `payload` field below.
   */
  "skill_install",
  /**
   * Per-step skill bin install outcome. After a `skill_install` event
   * with `errorTag = null` lands the skill's SKILL.md, the agent runs
   * `openclaw skills check --json`, parses the per-slug `install`
   * array, and shells out to `brew install <formula>` /
   * `npm install -g <package>` for each step. Each step produces one
   * `skill_bin_install` event with `errorTag` of `null` (applied /
   * skipped), `"SkillBinInstallFailed"` (brew or npm non-zero), or
   * `"SkillBinInstallUnsupported"` (manual / unknown kind). The
   * per-step detail (`slug`, `stepId`, `kind`, `status`, `exitCode`,
   * `stdoutTail`, `stderrTail`) rides on the optional `payload` field
   * below.
   */
  "skill_bin_install",
  /**
   * Per-archive private-skill delivery outcome. Each entry on
   * `RenderedProfile.skillArchives` produces exactly one
   * `skill_archive_install` event after clawd downloads the archive via
   * `mgmt.fetchSkillArchive`, verifies its `sha256` over the decoded
   * bytes, and stages it into the local OpenClaw gateway. `errorTag` is
   * null on success and carries the failure tag
   * (`FetchSkillArchiveFailed`, `Sha256Mismatch`, `GatewayConnectFailed`,
   * or `SkillArchiveInstallFailed`) otherwise. The per-archive detail
   * (`slug`, `version`, `sha256`, `status`, `stage`, `errorMessage`)
   * rides on the optional `payload` field below.
   */
  "skill_archive_install",
  /**
   * Per-step `runCliBootstrap` outcome. Each entry on
   * `RenderedProfile.cliBootstrap` produces exactly one `cli_bootstrap`
   * event — `errorTag` is null on success / skip and
   * `"CliBootstrapFailed"` on non-zero exit or unrecoverable
   * fetch/exec failure. The per-step detail (`id`, `runOnceKey`,
   * `exitCode`, `stdoutTail`, `stderrTail`) rides on the optional
   * `payload` field below; the agent treats matching
   * `(installationId, runOnceKey)` rows as the idempotency record —
   * subsequent applies short-circuit when a prior `cli_bootstrap`
   * event with `errorTag = null` already exists for the key.
   */
  "cli_bootstrap",
  /**
   * Per-step generic connector-pipeline outcome. Each
   * `RenderedProfile.connectorPlan` entry produces exactly one
   * `connector_step` event with the embedded skill / file / command
   * outcomes carried on the `payload` field.
   */
  "connector_step",
  /**
   * Per-run restic backup outcome (CLA-541). The clawd backup fiber
   * emits exactly one `backup_snapshot` event after each backup cycle —
   * `errorTag` is null on a successful snapshot and carries the failure
   * tag (`ResticExec`, `ResticUnavailable`, …) otherwise. The structured
   * `BackupRunResult` (`status`, `snapshotId`, `bytesAdded`) rides on the
   * optional `payload` field below.
   */
  "backup_snapshot",
  /**
   * Per-step OpenClaw core-upgrade progress (CLA-992). clawd's
   * OpenclawUpgrade step runner emits one `openclaw_upgrade_step` event per
   * phase transition (`started` then a terminal `done`/`failed`/`skipped`)
   * so the admin side can watch a canary progress. `errorTag` carries the
   * failed step name and the structured `UpgradeStepEvent`
   * (`step`, `status`, `durationMs`, `detail`) rides on the optional
   * `payload` field below.
   */
  "openclaw_upgrade_step",
  /**
   * Terminal OpenClaw core-upgrade outcome (CLA-992). Emitted exactly once
   * per `openclaw.upgrade` / `openclaw.rollback` command after the step
   * runner returns — `errorTag` is null for an `Upgraded` outcome and the
   * outcome `_tag` (`Refused` / `RolledBack` / `Wedged`) otherwise. The full
   * `UpgradeOutcome` rides on the optional `payload` field below;
   * `db.configLog.findLatestOpenclawUpgradeEvent` reads the newest such row.
   */
  "openclaw_upgrade",
  /**
   * Per-step backup-restore progress (CLA-1071). clawd's restore step runner
   * emits one `backup_restore_step` event per phase transition (`started`
   * then a terminal `done`/`failed`/`skipped`). The structured
   * `BackupRestoreStepEvent` rides on the optional `payload` field below.
   */
  "backup_restore_step",
  /**
   * Terminal backup-restore outcome (CLA-1071). Emitted exactly once per
   * `backup.restore` command — `errorTag` is null for a `Restored` outcome
   * and the outcome `_tag` (`Refused` / `RolledBack` / `Wedged`) otherwise;
   * the full `BackupRestoreOutcome` rides on `payload`. The api-side
   * `backup_restore_requested` row is written by the api itself, never
   * reported by the box, so it is deliberately absent here.
   */
  "backup_restore",
);
export type MigrationEventName = Schema.Schema.Type<typeof MigrationEventName>;

/**
 * Input payload for `mgmt.reportMigrationEvent`. The agent calls this
 * once per migration step (typically 3–4 calls per migrated install).
 *
 * @since 1.0.0
 */
export const ReportMigrationEventInput = Schema.Struct({
  installationId: Schema.String,
  publishId: Schema.String,
  version: Schema.Int,
  event: MigrationEventName,
  errorTag: Schema.NullOr(Schema.String),
  errorMessage: Schema.NullOr(Schema.String),
  /**
   * Optional structured per-event payload. The `plugin_install` event
   * carries `{spec, exitCode, stdoutTail, stderrTail}`; the
   * `skill_install` event carries `{slug, exitCode, stdoutTail,
   * stderrTail}`. The pre-existing migration_* events leave this
   * undefined.
   *
   * @since 1.0.0
   */
  payload: Schema.optional(Schema.Unknown),
});
export type ReportMigrationEventInput = Schema.Schema.Type<typeof ReportMigrationEventInput>;

/**
 * Wire response for `mgmt.reportMigrationEvent`.
 *
 * @since 1.0.0
 */
export const ReportMigrationEventResponse = Schema.Struct({
  ok: Schema.Boolean,
});
export type ReportMigrationEventResponse = Schema.Schema.Type<typeof ReportMigrationEventResponse>;

/**
 * Input payload for `mgmt.appendConfigLog`. Streamed by the on-node
 * config git-watch loop (`@fm/clawinstall-mgmt-agent`, hosted inside
 * clawd) for every out-of-band OpenClaw config change it detects —
 * autosync commits, sustainably-green ratchets, hourly tags, hard-down
 * reverts, and clobbered/doctor alerts. Every `eventKind` value the
 * loop emits already exists in the `installation_config_event_kind` pg
 * enum, so no enum migration is required.
 *
 * @since 1.0.0
 */
export const AppendConfigLogInput = Schema.Struct({
  installationId: Schema.String,
  sha: Schema.NullOr(Schema.String),
  eventKind: InstallationConfigEventKind,
  author: Schema.String,
  layer: Schema.NullOr(Schema.String),
  message: Schema.NullOr(Schema.String),
  filesChanged: Schema.NullOr(Schema.Unknown),
  trigger: Schema.NullOr(Schema.Unknown),
});
export type AppendConfigLogInput = Schema.Schema.Type<typeof AppendConfigLogInput>;

/**
 * Wire response for `mgmt.appendConfigLog`. Acknowledges the append —
 * the audit row on `installation_config_log` is the durable record.
 *
 * @since 1.0.0
 */
export const AppendConfigLogResponse = Schema.Struct({
  ok: Schema.Boolean,
});
export type AppendConfigLogResponse = Schema.Schema.Type<typeof AppendConfigLogResponse>;

/**
 * One pending OpenClaw device-pairing request, as normalized by the on-node
 * agent from `openclaw devices list --json`. A new browser login parks such a
 * request on the gateway until a trusted surface (the dashboard) approves it.
 *
 * `requestId` is the opaque id passed back to `openclaw devices approve/reject`.
 * `fingerprint` is the device fingerprint the customer visually confirms.
 * `scope` names the requested access scope for scope-upgrade pairings (null for
 * a plain first-device login). `label` is an optional human descriptor the
 * gateway may attach (browser/OS). Both nullable fields tolerate gateways/CLI
 * versions that omit them. No timestamp is carried — the snapshot is ephemeral
 * (Valkey TTL) and re-reported on every dashboard poll.
 *
 * @since 1.0.0
 */
export const PendingPairingRequest = Schema.Struct({
  requestId: Schema.String,
  fingerprint: Schema.String,
  scope: Schema.NullOr(Schema.String),
  label: Schema.NullOr(Schema.String),
});
export type PendingPairingRequest = Schema.Schema.Type<typeof PendingPairingRequest>;

/**
 * Input payload for `mgmt.reportPendingPairing`. The on-node agent pushes its
 * current pending device-pairing set in response to a `devices.list` command.
 * `installationId` is re-asserted against the authenticated gateway token so a
 * hostile box cannot report pairings for another install; `requests` is the
 * full current set (an empty array clears the dashboard).
 *
 * @since 1.0.0
 */
export const ReportPendingPairingInput = Schema.Struct({
  installationId: Schema.String,
  requests: Schema.Array(PendingPairingRequest),
});
export type ReportPendingPairingInput = Schema.Schema.Type<typeof ReportPendingPairingInput>;

/**
 * Wire response for `mgmt.reportPendingPairing`. Acknowledges the snapshot
 * store; the Valkey key is the durable-until-TTL record the dashboard reads.
 *
 * @since 1.0.0
 */
export const ReportPendingPairingResponse = Schema.Struct({
  ok: Schema.Boolean,
});
export type ReportPendingPairingResponse = Schema.Schema.Type<typeof ReportPendingPairingResponse>;

/**
 * Input payload for `mgmt.fetchBackupCredentials`. Empty — the target
 * installation is resolved from the authenticated gateway token
 * (`MgmtAgentInstallation`), never from a client-supplied field, so a
 * hostile box cannot mint credentials scoped to another install.
 *
 * @since 1.0.0
 */
export const FetchBackupCredentialsInput = Schema.Struct({});
export type FetchBackupCredentialsInput = Schema.Schema.Type<typeof FetchBackupCredentialsInput>;

/**
 * Wire response for `mgmt.fetchBackupCredentials`. The schema moved into
 * `@fm/cluster/services/backup-creds` (CLA-555) alongside the mint
 * helpers that produce it; re-exported here so api consumers keep
 * importing from `rpc/mgmt.ts`.
 *
 * @since 1.0.0
 */
export { FetchBackupCredentialsResponse };

/**
 * Input payload for `mgmt.fetchSkillArchive`. `installationId` is
 * re-asserted against the authenticated gateway token; `(slug, version)`
 * names the archive to download.
 *
 * @since 1.0.0
 */
export const FetchSkillArchiveInput = Schema.Struct({
  installationId: Schema.String,
  slug: Schema.String,
  version: Schema.String,
});
export type FetchSkillArchiveInput = Schema.Schema.Type<typeof FetchSkillArchiveInput>;

/**
 * Wire response for `mgmt.fetchSkillArchive`. Carries the raw zip bytes
 * as base64 alongside the integrity metadata clawd verifies before
 * staging the archive into the local OpenClaw gateway. `sha256` is the
 * hex digest of the raw (decoded) zip bytes and `sizeBytes` their raw
 * byte length.
 *
 * @since 1.0.0
 */
export const FetchSkillArchiveResponse = Schema.Struct({
  slug: Schema.String,
  version: Schema.String,
  sha256: Schema.String,
  sizeBytes: Schema.Number,
  archiveBase64: Schema.String,
});
export type FetchSkillArchiveResponse = Schema.Schema.Type<typeof FetchSkillArchiveResponse>;

/**
 * One security finding reported by clawd's on-box self-sweep (CLA-754).
 * `check` is a stable finding-type identifier, `severity` drives whether
 * the control plane pages a human (`high`) or only persists (`medium`),
 * and `detail` carries the human-readable specifics.
 *
 * @since 1.0.0
 */
export const SecurityFinding = Schema.Struct({
  check: Schema.String,
  severity: Schema.Literal("high", "medium"),
  detail: Schema.String,
});
export type SecurityFinding = Schema.Schema.Type<typeof SecurityFinding>;

/**
 * Input payload for `mgmt.reportSecurityFinding`. `installationId` is
 * re-asserted against the authenticated gateway token so a hostile box
 * cannot report findings for another install; `findings` is the non-empty
 * set from one sweep cycle (a clean sweep never calls this RPC).
 *
 * @since 1.0.0
 */
export const ReportSecurityFindingInput = Schema.Struct({
  installationId: Schema.String,
  findings: Schema.Array(SecurityFinding),
});
export type ReportSecurityFindingInput = Schema.Schema.Type<typeof ReportSecurityFindingInput>;

/**
 * Wire response for `mgmt.reportSecurityFinding`. Acknowledges the
 * report — the persisted `installation_logs` rows are the durable record.
 *
 * @since 1.0.0
 */
export const ReportSecurityFindingResponse = Schema.Struct({
  ok: Schema.Boolean,
});
export type ReportSecurityFindingResponse = Schema.Schema.Type<typeof ReportSecurityFindingResponse>;

/**
 * One diagnostic finding reported by clawd's on-box doctor loop / log-signature
 * probe. `checkId` is a stable finding-type identifier, `message` the
 * human-readable specifics, and `severity` drives whether the control plane
 * pages a human (`error`) or only persists (`info`/`warning`).
 *
 * Severity is doctor's own vocabulary (`info | warning | error`) —
 * deliberately NOT `SecurityFinding`'s `high | medium`, because doctor is the
 * sole producer and its `error` is the pageable value. `observedAt` is when the
 * on-box probe recorded the finding. Shape verified live against the pinned
 * openclaw 2026.6.9 `doctor --lint --json` output.
 *
 * @since 1.0.0
 */
export const DiagnosticFinding = Schema.Struct({
  severity: Schema.Literal("info", "warning", "error"),
  checkId: Schema.String,
  message: Schema.String,
  path: Schema.optional(Schema.String),
  fixHint: Schema.optional(Schema.String),
  observedAt: Schema.DateTimeUtc,
});
export type DiagnosticFinding = Schema.Schema.Type<typeof DiagnosticFinding>;

/**
 * Input payload for `mgmt.reportDiagnosticFinding`. `installationId` is
 * re-asserted against the authenticated gateway token so a hostile box cannot
 * report findings for another install; `findings` is the finding set from one
 * doctor pass (a clean pass never calls this RPC).
 *
 * @since 1.0.0
 */
export const ReportDiagnosticFindingInput = Schema.Struct({
  installationId: Schema.String,
  findings: Schema.Array(DiagnosticFinding),
});
export type ReportDiagnosticFindingInput = Schema.Schema.Type<typeof ReportDiagnosticFindingInput>;

/**
 * Wire response for `mgmt.reportDiagnosticFinding`. Acknowledges the report —
 * the persisted `installation_logs` rows are the durable record.
 *
 * @since 1.0.0
 */
export const ReportDiagnosticFindingResponse = Schema.Struct({
  ok: Schema.Boolean,
});
export type ReportDiagnosticFindingResponse = Schema.Schema.Type<typeof ReportDiagnosticFindingResponse>;

/**
 * Input payload for `mgmt.reportConfigSnapshot`. Posted by clawd's config
 * snapshot loop (CLA-807) with the box-side-REDACTED `openclaw.json`
 * document — redaction happens on the box, before the wire; the server
 * must never see a secret value even transiently. `installationId` is
 * re-asserted against the authenticated gateway token so a hostile box
 * cannot report config for another install. `configHash` is the
 * client-computed canonical-JSON hash, stored without recomputation
 * (acceptable: the box controls the content either way). `redactedCount`
 * is logged, never persisted.
 *
 * @since 1.0.0
 */
export const ReportConfigSnapshotInput = Schema.Struct({
  installationId: Schema.String,
  redactedConfig: Schema.Unknown,
  configHash: Schema.String,
  capturedAt: Schema.DateTimeUtc,
  redactedCount: Schema.Int,
});
export type ReportConfigSnapshotInput = Schema.Schema.Type<typeof ReportConfigSnapshotInput>;

/**
 * Wire response for `mgmt.reportConfigSnapshot`. Acknowledges the report —
 * the `agent_installations` snapshot columns are the durable record.
 *
 * @since 1.0.0
 */
export const ReportConfigSnapshotResponse = Schema.Struct({ ok: Schema.Boolean });
export type ReportConfigSnapshotResponse = Schema.Schema.Type<typeof ReportConfigSnapshotResponse>;

/**
 * Wire response for `mgmt.reportBackupSnapshots`. Acknowledges the snapshot
 * store; the Valkey key is the durable-until-TTL record the admin view reads.
 *
 * @since 1.0.0
 */
export const ReportBackupSnapshotsResponse = Schema.Struct({ ok: Schema.Boolean });
export type ReportBackupSnapshotsResponse = Schema.Schema.Type<typeof ReportBackupSnapshotsResponse>;

/**
 * Wire input for `mgmt.reportAgentStats`: clawd's StatsLoop posts the
 * installation's OpenClaw memory, workspace and skills stats. The `stats`
 * schema is the SEAM-DB `OcStatsReport` (bounded: at most 20 memory agents
 * and 200 installed skills). Ownership-checked like every mgmt write — a
 * box cannot report stats for another install.
 *
 * @since 1.0.0
 */
export const ReportAgentStatsInput = Schema.Struct({
  installationId: Schema.String,
  stats: OcStatsReport,
});
export type ReportAgentStatsInput = Schema.Schema.Type<typeof ReportAgentStatsInput>;

/**
 * Wire response for `mgmt.reportAgentStats`. Acknowledges the upsert — the
 * `agent_installation_stats.oc_stats` column is the durable record.
 *
 * @since 1.0.0
 */
export const ReportAgentStatsResponse = Schema.Struct({ ok: Schema.Boolean });
export type ReportAgentStatsResponse = Schema.Schema.Type<typeof ReportAgentStatsResponse>;

/**
 * Management-agent RPC group. All endpoints require `MgmtAgentMiddleware`.
 *
 * @since 1.0.0
 */
export class MgmtAgentRpcs extends RpcGroup.make(
  Rpc.make("mgmt.fetchConnectionSecret", {
    payload: FetchConnectionSecretInput,
    success: FetchConnectionSecretResponse,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError, CryptoError),
  }).middleware(MgmtAgentMiddleware),
  Rpc.make("mgmt.fetchEffectiveProfile", {
    payload: FetchEffectiveProfileInput,
    success: FetchEffectiveProfileResponse,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(MgmtAgentMiddleware),
  Rpc.make("mgmt.reportProfileApplyResult", {
    payload: ReportProfileApplyResultInput,
    success: ReportProfileApplyResultResponse,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(MgmtAgentMiddleware),
  Rpc.make("mgmt.reportMigrationEvent", {
    payload: ReportMigrationEventInput,
    success: ReportMigrationEventResponse,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(MgmtAgentMiddleware),
  Rpc.make("mgmt.reportPendingPairing", {
    payload: ReportPendingPairingInput,
    success: ReportPendingPairingResponse,
    error: Schema.Union(Unauthorized, Forbidden, ValkeyError),
  }).middleware(MgmtAgentMiddleware),
  Rpc.make("mgmt.appendConfigLog", {
    payload: AppendConfigLogInput,
    success: AppendConfigLogResponse,
    error: Schema.Union(Unauthorized, DatabaseError, RecordNotFoundError, InvalidTriggerPayload),
  }).middleware(MgmtAgentMiddleware),
  Rpc.make("mgmt.fetchBackupCredentials", {
    payload: FetchBackupCredentialsInput,
    success: FetchBackupCredentialsResponse,
    error: Schema.Union(Unauthorized, Forbidden, BackupStorageNotConfigured, BackupCredentialMintFailed),
  }).middleware(MgmtAgentMiddleware),
  Rpc.make("mgmt.fetchSkillArchive", {
    payload: FetchSkillArchiveInput,
    success: FetchSkillArchiveResponse,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError, RecordNotFoundError),
  }).middleware(MgmtAgentMiddleware),
  Rpc.make("mgmt.reportSecurityFinding", {
    payload: ReportSecurityFindingInput,
    success: ReportSecurityFindingResponse,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(MgmtAgentMiddleware),
  Rpc.make("mgmt.reportDiagnosticFinding", {
    payload: ReportDiagnosticFindingInput,
    success: ReportDiagnosticFindingResponse,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(MgmtAgentMiddleware),
  Rpc.make("mgmt.reportConfigSnapshot", {
    payload: ReportConfigSnapshotInput,
    success: ReportConfigSnapshotResponse,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(MgmtAgentMiddleware),
  Rpc.make("mgmt.reportBackupSnapshots", {
    payload: ReportBackupSnapshotsInput,
    success: ReportBackupSnapshotsResponse,
    error: Schema.Union(Unauthorized, Forbidden, ValkeyError),
  }).middleware(MgmtAgentMiddleware),
  Rpc.make("mgmt.reportAgentStats", {
    payload: ReportAgentStatsInput,
    success: ReportAgentStatsResponse,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(MgmtAgentMiddleware),
) {}
